import BaseLayout from '@/layout/BaseLayout';

import BrandCard from '@/components/BrandCard';
import GridBrandCard from '@/components/layout/GridBrandCard';
import Loader from '@/components/Loader';
import { trpc } from '@/trpcClient';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { EBrandCategory } from '@shelflix/schemas/brand/enums';
import type { ListBrandFilterTypeWithValues } from '@shelflix/schemas/brand/list';
import { BrandDto } from '@shelflix/trpc/routes/brand/list/useCase';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BrandFilter } from './BrandFilter';

export function BrandList() {
  const [filters, setFilters] = useState<ListBrandFilterTypeWithValues>({});
  const [page] = useState(1);
  const pageSize = 100;

  const { data, isLoading, isError } = useQuery(
    trpc.brand.list.queryOptions({
      page,
      recordsPerPage: pageSize,
      ...(filters.search?.value && { search: { ...filters.search } }),
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
      ...(filters.sort && { sort: filters.sort }),
    })
  );

  const brands = data?.brands ?? [];

  const { isMobile } = useDeviceDetect();

  const location = useLocation();
  const hasShownToast = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.toastSuccess && !hasShownToast.current) {
      toast.success(`Alteração salva com sucesso`);
      hasShownToast.current = true;

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const groupedBrands = brands.reduce(
    (acc, brand) => {
      if (!acc[brand.category as EBrandCategory]) {
        acc[brand.category] = [];
      }
      acc[brand.category].push(brand);
      return acc;
    },
    {} as Record<string, BrandDto[]>
  );

  return (
    <BaseLayout>
      <section className="flex flex-col">
        <BrandFilter onChange={setFilters} />
        <section
          className="flex w-full flex-wrap overflow-hidden rounded-[5px]"
          style={{ gap: isMobile ? '8px' : '28px' }}
        >
          {isLoading && <Loader message="Carregando marcas..." />}
          {isError && <p className="p-4 text-red-500">Erro ao carregar marcas.</p>}
          {!isLoading &&
            !isError &&
            !!brands.length &&
            Object.entries(groupedBrands)
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([category, brands]) => (
                <>
                  <h2 className="col-span-full mt-6 flex items-center gap-2 text-xl font-semibold">
                    <div className="h-6 w-2 rounded-full bg-orange-500"></div>
                    {category === EBrandCategory.MULTIBRAND ? 'Multimarcas' : 'Exclusivas'}
                    <span className="text-sm font-normal text-gray-500">
                      ({brands.length} materi{brands.length > 1 ? 'ais' : 'al'})
                    </span>
                  </h2>
                  <GridBrandCard>
                    {brands.map((brand) => (
                      <BrandCard className="w-[48.5%] md:w-[242px]" key={brand.uuid} {...brand} />
                    ))}
                  </GridBrandCard>
                </>
              ))}
          {/* brands.map((brand) => <BrandCard className="w-[48.5%] md:w-[242px]" key={brand.uuid} {...brand} />)} */}
          {!isLoading && !isError && !brands.length && <p className="p-4">Nenhuma marca cadastrada</p>}
        </section>
        {/* <TablePagination itemsPerPage={pageSize} page={page} totalItems={totalItems} onPageChange={setPage} /> */}
      </section>
    </BaseLayout>
  );
}
